import { Language } from "@/api/data-contracts";
import { ChangeHandler } from "@/lib/modelstate";
import { extractEvent } from "@/lib/utils";

/**
 * Type guard: narrows an unknown value to a valid {@link Language} enum member.
 */
const isLanguage = (value: unknown): value is Language =>
    typeof value === "string" && (Object.values(Language) as string[]).includes(value);

/**
 * Wraps a model-state {@link ChangeHandler} so that changing the `language` field additionally
 * switches the live app language.
 *
 * The wrapped handler ALWAYS calls the original handler first, so the selected language is written
 * into the model and gets persisted when the registration is submitted. On top of that, it calls
 * `onLanguageChange` — but only when the changed field is `language` and its new value is a valid
 * {@link Language}. The switch is a pure, transient UI side effect with no persistence of its own:
 * if the user abandons the registration, nothing is stored and the invitation language remains in
 * effect.
 *
 * @param onChange         the original model-state change handler (model update)
 * @param onLanguageChange callback that switches the live app language, invoked only on a valid
 *                         `language` field change
 * @returns a new change handler with the live-switch side effect layered on top
 */
export const withLanguageAwareChange =
    (onChange: ChangeHandler, onLanguageChange: (language: Language) => void): ChangeHandler =>
    (event) => {
        onChange(event);

        const extracted = extractEvent(event);
        if (extracted === null) {
            return;
        }
        const [name, value] = extracted;
        if (name === "language" && isLanguage(value)) {
            onLanguageChange(value);
        }
    };
